/**
 * The pages of a PDF the model reads itself ("3", "2-5", "1,4-6"), checked as the server's
 * `attachments.parse_pages` checks them, to say what is wrong before a send. The server checks
 * again, and also caps the pages of one message together.
 */

/** The most pages of a PDF one message may send (the server's AGENT_NATIVE_PAGES_PER_CALL). */
export const MAX_PAGES = 10;
const MAX_CHARS = 100;
const PAGES = /^\d+(?:-\d+)?(?:,\d+(?:-\d+)?)*$/;
const FORMAT = 'Give pages as numbers and ranges, such as "3", "2-5" or "1,4-6".';

/** The pages as sent (spaces dropped), or what is wrong with them. Empty means the whole file. */
export function checkPages(text: string): { pages: string | null } | { problem: string } {
  const compact = text.replace(/\s+/g, "");
  if (!compact) return { pages: null };
  if (compact.length > MAX_CHARS || !PAGES.test(compact)) return { problem: FORMAT };
  const ranges: [number, number][] = [];
  for (const item of compact.split(",")) {
    const [first, last = first] = item.split("-").map(Number);
    if (first < 1 || last < first) {
      return { problem: `${FORMAT} Pages start at 1, and a range runs low to high.` };
    }
    ranges.push([first, last]);
  }
  // Counted as the server counts them: overlapping and touching ranges once.
  ranges.sort((a, b) => a[0] - b[0]);
  let count = 0;
  let end = 0;
  for (const [first, last] of ranges) {
    if (last <= end) continue;
    count += last - Math.max(first, end + 1) + 1;
    end = last;
  }
  if (count > MAX_PAGES) return { problem: `Send at most ${MAX_PAGES} pages of a PDF at a time.` };
  return { pages: compact };
}

/** How long a count may take: Send waits on it, so one that never ends must not hold it. */
export const PAGE_COUNT_TIMEOUT_MS = 15_000;

/**
 * A PDF whose pages took longer than PAGE_COUNT_TIMEOUT_MS to count: taken as too many, so it
 * gets its first MAX_PAGES. A shorter one is sent whole: the server cuts a send's pages to the PDF's
 * own (`attachments.clamp_pages`).
 */
export const TIMED_OUT = "timedOut";

/** A PDF's pages: TIMED_OUT when they took too long to count, null when they could not be. */
export type PageCount = number | typeof TIMED_OUT | null;

/** A request's failure was its timeout running out (axios's codes), not an answer or an error. */
export const requestTimedOut = (error: unknown) =>
  ["ECONNABORTED", "ETIMEDOUT"].includes((error as { code?: unknown } | null)?.code as string);

/**
 * A PDF's page count, from pdf.js (its lazy chunk): TIMED_OUT when that takes longer than
 * PAGE_COUNT_TIMEOUT_MS, null when it does not open. The bytes go to pdf.js's worker: they are not
 * usable here after.
 */
export async function pdfPageCount(bytes: ArrayBuffer): Promise<PageCount> {
  try {
    const { openPdf } = await import("./preview/pdfjs");
    const loading = openPdf(new Uint8Array(bytes));
    // Destroyed on a timeout, it rejects with no one waiting.
    loading.promise.catch(() => {});
    let timer: ReturnType<typeof setTimeout> | undefined;
    const late = new Promise<typeof TIMED_OUT>((resolve) => {
      timer = setTimeout(() => resolve(TIMED_OUT), PAGE_COUNT_TIMEOUT_MS);
    });
    try {
      const pdf = await Promise.race([loading.promise, late]);
      return pdf === TIMED_OUT ? pdf : pdf.numPages;
    } finally {
      clearTimeout(timer);
      void loading.destroy();
    }
  } catch {
    return null;
  }
}
